import React, { useState } from "react";
import { Box, Drawer, IconButton, Tooltip, Typography } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import CorporateFareRoundedIcon from "@mui/icons-material/CorporateFareRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
// Geçici olarak Limit Tanımları sekmesi kapalı.
// import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import { useNavigate, useLocation } from "react-router-dom";
import KEYCLOAK_CONFIG from "../../config/keycloakConfig";
import { DP, DP_FONT_FAMILY, SIDEBAR_WIDTH } from "./constants";

// Vendai Digital Pay'e özel gruplu navigasyon.
export const NAV_GROUPS = [
  {
    label: "İzleme",
    items: [
      { icon: DashboardRoundedIcon, text: "Genel Bakış", path: "/dashboard", desc: "Sistem geneli özet ve son hareketler." },
      { icon: CreditCardRoundedIcon, text: "Kart İzleme", path: "/cards", desc: "Kart bakiyelerini izleyin ve filtreleyin." },
    ],
  },
  {
    label: "Yönetim",
    items: [
      { icon: CorporateFareRoundedIcon, text: "Çatı Şirketler", path: "/auth-companies", desc: "Çatı şirketleri ekleyin, düzenleyin ve listeleyin." },
      { icon: BusinessCenterRoundedIcon, text: "Firmalar", path: "/firms", desc: "Firmaları çatı şirkete bağlayarak yönetin." },
      { icon: AccountTreeRoundedIcon, text: "Departmanlar", path: "/departments", desc: "Departmanları firmalara bağlayın." },
      { icon: BadgeRoundedIcon, text: "Personel Yönetimi", path: "/personal", desc: "Personel kaydı, kart eşleme ve toplu yükleme." },
      // Geçici olarak gizlendi; yeniden açılırken App route'u ve entity eşlemesi de etkinleştirilmeli.
      // { icon: TuneRoundedIcon, text: "Limit Tanımları", path: "/limit-definitions", desc: "Departman bazında standart ve İSG hakedişi." },
    ],
  },
  {
    label: "İşlemler",
    items: [
      { icon: AccountBalanceWalletRoundedIcon, text: "Bakiye & Ödeme", path: "/payments", desc: "Tekil ve toplu bakiye yükleme." },
      { icon: ReceiptLongRoundedIcon, text: "İşlem Geçmişi", path: "/transactions", desc: "Bakiye hareketlerini görüntüleyin ve dışa aktarın." },
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items);
export const navItemFor = (pathname) =>
  ALL_ITEMS.find((i) => i.path === pathname) || ALL_ITEMS[0];

function getStoredUserInfo() {
  const userName = localStorage.getItem("userName") || "";
  const companyName = localStorage.getItem("companyName") || "";
  const rolesRaw = localStorage.getItem("userRoles") || "";
  let roles = [];

  try {
    const parsed = JSON.parse(rolesRaw);
    roles = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
  } catch {
    roles = rolesRaw
      .replaceAll("[", "")
      .replaceAll("]", "")
      .replaceAll('"', "")
      .split(",")
      .map((role) => role.trim())
      .filter(Boolean);
  }

  const hasSuperAdmin = roles.some(
    (role) => String(role).toLowerCase() === "superadmin"
  );
  const visibleRoles = hasSuperAdmin ? ["superAdmin"] : roles;
  const formatRole = (role) =>
    String(role)
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (letter) => letter.toUpperCase());

  return {
    userName,
    displayName: userName ? userName.split("@")[0] : "Kullanıcı",
    companyName: companyName || "Client seçilmedi",
    hasSuperAdmin,
    roleText: visibleRoles.length
      ? visibleRoles.map(formatRole).join(", ")
      : "Rol bilgisi yok",
  };
}

function SidebarContent({ pathname, onNavigate, onLogout }) {
  const userInfo = getStoredUserInfo();

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        bgcolor: DP.sidebar,
        backgroundImage:
          "radial-gradient(circle at 10% 0%, rgba(14,165,233,.24), transparent 34%), linear-gradient(180deg, #0b2444 0%, #071729 100%)",
        display: "flex",
        flexDirection: "column",
        color: DP.sidebarText,
        fontFamily: DP_FONT_FAMILY,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.4,
          px: 2.5,
          py: 2.7,
          color: DP.sidebarActive,
          fontWeight: 700,
          fontSize: "1.05rem",
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "13px",
            display: "grid",
            placeItems: "center",
            color: "#fff",
            background: "linear-gradient(135deg, #0b74de 0%, #22b8cf 100%)",
            boxShadow: "0 10px 28px rgba(11,116,222,.34)",
            flexShrink: 0,
          }}
        >
          <PaymentsRoundedIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: "#fff",
              fontSize: "1rem",
              fontWeight: 750,
              lineHeight: 1.2,
              letterSpacing: "-0.025em",
            }}
          >
            Vendai Digital Pay
          </Typography>
          <Typography
            sx={{
              color: DP.sidebarText,
              fontSize: ".69rem",
              lineHeight: 1.35,
              letterSpacing: "0.015em",
              mt: 0.35,
            }}
          >
            Ödeme yönetim platformu
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 1.5 }}>
        {NAV_GROUPS.map((group) => (
          <Box key={group.label} sx={{ mb: 1.65 }}>
            <Typography
              sx={{
                px: 1.5,
                py: 1,
                fontSize: "0.65rem",
                fontWeight: 750,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: DP.sidebarText,
                opacity: 0.72,
              }}
            >
              {group.label}
            </Typography>
            {group.items
              .filter(
                (item) =>
                  userInfo.hasSuperAdmin ||
                  !["/auth-companies", "/firms"].includes(item.path)
              )
              .map((item) => {
              const active = pathname === item.path;
              const NavIcon = item.icon;
              return (
                <Box
                  key={item.path}
                  onClick={() => onNavigate(item.path)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    px: 1.5,
                    py: 1.15,
                    mb: 0.4,
                    borderRadius: DP.radius,
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    lineHeight: 1.35,
                    letterSpacing: "-0.005em",
                    color: active ? DP.sidebarActive : DP.sidebarText,
                    bgcolor: active ? "rgba(14,165,233,.2)" : "transparent",
                    fontWeight: active ? 650 : 450,
                    position: "relative",
                    transition: "background-color .18s ease, color .18s ease, transform .18s ease",
                    "&::before": active
                      ? {
                          content: '""',
                          position: "absolute",
                          left: -6,
                          width: 3,
                          height: 22,
                          borderRadius: 4,
                          bgcolor: "#38bdf8",
                        }
                      : undefined,
                    "&:hover": {
                      bgcolor: DP.sidebarHover,
                      color: DP.sidebarActive,
                      transform: "translateX(2px)",
                    },
                  }}
                >
                  <NavIcon sx={{ fontSize: 19, opacity: active ? 1 : 0.82 }} />
                  <span>{item.text}</span>
                </Box>
              );
              })}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          p: 2,
          borderTop: "1px solid rgba(255,255,255,.09)",
          bgcolor: "rgba(0,0,0,.12)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              title={userInfo.userName || userInfo.displayName}
              sx={{
                color: "#fff",
                fontSize: ".74rem",
                fontWeight: 680,
                lineHeight: 1.35,
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {userInfo.userName || userInfo.displayName}
            </Typography>
          </Box>

          <Tooltip title="Çıkış yap" placement="top" arrow>
            <IconButton
              aria-label="Çıkış yap"
              onClick={onLogout}
              size="small"
              sx={{
                color: DP.sidebarText,
                border: "1px solid rgba(255,255,255,.1)",
                borderRadius: "10px",
                "&:hover": {
                  color: "#fff",
                  bgcolor: "rgba(239,68,68,.18)",
                  borderColor: "rgba(248,113,113,.32)",
                },
              }}
            >
              <LogoutRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            mt: 1.4,
            pt: 1.2,
            borderTop: "1px solid rgba(255,255,255,.07)",
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 1.25,
          }}
        >
          <Box
            onClick={() => onNavigate("/client")}
            sx={{
              minWidth: 0,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover strong": { color: "#fff" },
            }}
          >
            <Typography
              component="strong"
              title={userInfo.companyName}
              sx={{
                display: "block",
                color: "#d7e2f5",
                fontSize: ".7rem",
                fontWeight: 600,
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                transition: "color .16s ease",
              }}
            >
              {userInfo.companyName}
            </Typography>
          </Box>
          <Box
            sx={{
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              title={userInfo.roleText}
              sx={{
                color: "#d7e2f5",
                fontSize: ".7rem",
                fontWeight: 600,
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {userInfo.roleText}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function DigitalPayLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const current = navItemFor(location.pathname);

  const handleLogout = () => {
    const idToken = localStorage.getItem("idToken");
    localStorage.clear();
    if (idToken) {
      window.location.href =
        `${KEYCLOAK_CONFIG.realmUrl}/protocol/openid-connect/logout` +
        `?id_token_hint=${encodeURIComponent(idToken)}` +
        `&post_logout_redirect_uri=${encodeURIComponent(KEYCLOAK_CONFIG.redirectUri)}`;
    } else {
      navigate("/login");
    }
  };

  const go = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: DP.bg,
        fontFamily: DP_FONT_FAMILY,
        backgroundImage:
          "radial-gradient(circle at 95% 0%, rgba(11,116,222,.07), transparent 28%)",
      }}
    >
      {/* Masaüstü sidebar */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            inset: "0 auto 0 0",
            width: SIDEBAR_WIDTH,
            zIndex: 20,
          }}
        >
          <SidebarContent
            pathname={location.pathname}
            onNavigate={go}
            onLogout={handleLogout}
          />
        </Box>
      </Box>

      {/* Mobil sidebar */}
      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{ display: { xs: "block", md: "none" } }}
        PaperProps={{ sx: { border: 0, width: SIDEBAR_WIDTH } }}
      >
        <SidebarContent
          pathname={location.pathname}
          onNavigate={go}
          onLogout={handleLogout}
        />
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
        {/* Üst bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, md: 4 },
            py: 1.75,
            bgcolor: "rgba(255,255,255,.88)",
            borderBottom: `1px solid ${DP.border}`,
            position: "sticky",
            top: 0,
            zIndex: 10,
            backdropFilter: "blur(16px)",
          }}
        >
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{ display: { xs: "inline-flex", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: DP.primary,
                fontSize: ".66rem",
                fontWeight: 750,
                letterSpacing: ".11em",
                textTransform: "uppercase",
                mb: 0.2,
              }}
            >
              Vendai Digital Pay
            </Typography>
            <Typography
              sx={{
                fontWeight: 750,
                color: DP.textMain,
                fontSize: { xs: "1.05rem", sm: "1.18rem" },
                lineHeight: 1.25,
                letterSpacing: "-0.025em",
              }}
            >
              {current.text}
            </Typography>
            <Typography
              sx={{
                color: DP.textMuted,
                fontSize: "0.82rem",
                lineHeight: 1.45,
                letterSpacing: "0.002em",
                mt: 0.2,
              }}
            >
              {current.desc}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />
        </Box>

        <Box
          sx={{
            width: "100%",
            maxWidth: 1600,
            mx: "auto",
            boxSizing: "border-box",
            px: { xs: 2, md: 4 },
            py: { xs: 2.5, md: 3.5 },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
